import { Link } from "react-router-dom";
import {
  ArrowRight,
  Shield,
  Target,
  Eye,
  Award,
  Users,
  CheckCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { images } from "../data/mock";
import { useSiteConfig } from "../contexts/SiteConfigContext";
import { usePageSEO } from "../hooks/usePageSEO";

const iconMap = {
  Shield,
  Target,
  Eye,
  Award,
  Users,
};

const values = [
  {
    icon: Shield,
    title: "Integrity",
    description:
      "We uphold the highest ethical standards in every examination, ensuring objectivity and fairness throughout the process.",
  },
  {
    icon: Eye,
    title: "Discretion",
    description:
      "Absolute confidentiality is non-negotiable. Your privacy is protected at every stage — from consultation to final report.",
  },
  {
    icon: Target,
    title: "Accuracy",
    description:
      "We employ scientifically validated methods and state-of-the-art equipment to deliver the most reliable results possible.",
  },
  {
    icon: Users,
    title: "Professionalism",
    description:
      "Every client interaction is conducted with respect, empathy, and the highest level of professional courtesy.",
  },
];

const credentials = [
  "Certified Polygraph Examiner",
  "Accredited by professional polygraph associations",
  "Ongoing professional development and training",
  "Computerised polygraph system certified",
  "POPIA compliant practices",
  "Member of industry professional bodies",
];

export default function AboutPage() {
  const { images, about } = useSiteConfig();
  
  usePageSEO({
    title: "About Us",
    description: "Learn about The Lie Detective - a certified polygraph examiner committed to uncovering truth with scientific precision, professional integrity, and unwavering confidentiality.",
    path: "/about"
  });
  
  const aboutValues = about?.values || values;
  const aboutCredentials = about?.credentials || credentials;
  const storyParagraphs = about?.storyParagraphs || [
    "The Lie Detective was founded on a simple yet powerful belief: everyone deserves access to the truth. As a certified polygraph examiner based in South Africa, I bring years of professional experience, rigorous training, and genuine passion for investigative science to every examination.",
    "Whether serving corporate clients requiring pre-employment screening, legal professionals seeking case support, or individuals navigating personal matters — my approach combines scientific methodology with human understanding.",
    "Every examination is conducted with the utmost professionalism, ensuring accurate results delivered in a confidential, respectful environment. The trust my clients place in me is something I never take lightly.",
  ];
  const missionText = about?.mission || "To provide South Africa with the highest standard of polygraph examination services — combining cutting-edge technology, professional expertise, and an unwavering commitment to confidentiality and ethical practice.";
  const heroSubtitle = about?.heroSubtitle || "A dedicated, certified polygraph examiner committed to uncovering truth with scientific precision, professional integrity, and unwavering confidentiality.";

  return (
    <main>
      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-[#0A0A0F]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#DC2626]/[0.03] to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-[#DC2626] font-semibold tracking-[0.3em] text-xs mb-4 uppercase">
            About Us
          </p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            The Person Behind
            <br />
            <span className="text-[#DC2626]">The Truth</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl leading-relaxed">
            {heroSubtitle}
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="bg-[#0A0A0F] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-[#DC2626]/[0.02] rounded-3xl blur-3xl" />
              <img
                src={images.desk}
                alt="The Lie Detective at work"
                className="relative rounded-2xl w-full h-[500px] object-cover shadow-2xl shadow-black/50"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#0A0A0F]/60 via-transparent to-transparent" />
            </div>

            <div className="order-1 lg:order-2">
              <p className="text-[#DC2626] font-semibold tracking-[0.3em] text-xs mb-4 uppercase">
                Our Story
              </p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
                Dedicated to Truth
              </h2>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                {storyParagraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-[#0D0D14] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-[#DC2626] font-semibold tracking-[0.3em] text-xs mb-4 uppercase">
              Our Mission
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
              Truth. Integrity. Clarity.
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed">
              {missionText}
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#0A0A0F] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-xl mb-16">
            <p className="text-[#DC2626] font-semibold tracking-[0.3em] text-xs mb-4 uppercase">
              Our Values
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              What We Stand For
            </h2>
            <p className="text-gray-400 leading-relaxed">
              Every aspect of our service is guided by core principles that
              ensure the highest quality experience for our clients.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {aboutValues.map((value, i) => {
              const Icon = (typeof value.icon === 'string' ? iconMap[value.icon] : value.icon) || Shield;
              return (
                <div
                  key={i}
                  className="group bg-[#111118] border border-white/[0.04] rounded-2xl p-8 hover:border-[#DC2626]/20 transition-all duration-500"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#DC2626]/[0.08] flex items-center justify-center mb-5 group-hover:bg-[#DC2626]/[0.15] transition-colors duration-300">
                    <Icon className="h-5 w-5 text-[#DC2626]" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="bg-[#0D0D14] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#DC2626] font-semibold tracking-[0.3em] text-xs mb-4 uppercase">
                Qualifications
              </p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
                Credentials &amp; Certification
              </h2>
              <p className="text-gray-400 leading-relaxed mb-8">
                Our examiner holds recognised certifications and maintains
                ongoing professional development to ensure the highest
                standards of service delivery.
              </p>
              <ul className="space-y-4">
                {aboutCredentials.map((cred, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-[#DC2626] shrink-0" />
                    <span className="text-gray-300 text-sm">{cred}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src={images.office}
                alt="Professional office"
                className="rounded-2xl w-full h-[400px] object-cover shadow-2xl shadow-black/50"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#0D0D14]/50 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0A0F] py-24 lg:py-28 border-t border-white/[0.04]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Work Together?
          </h2>
          <p className="text-gray-400 text-lg mb-10 leading-relaxed">
            Get in touch to discuss your requirements. All consultations are
            confidential and obligation-free.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white px-10 h-13 text-base font-medium transition-colors duration-200"
            >
              Request a Quote
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
